/** Zeigt informative HTML-Inhalte aus `data-tooltip` ausschließlich bei Maus- oder Stift-Hover an */
export default class Tooltip {
    private static readonly SHOW_DELAY_MS = 500;
    private static readonly CLOSE_DELAY_MS = 100;
    private static readonly VIEWPORT_MARGIN_PX = 12;
    private static readonly TRIGGER_GAP_PX = 4;
    private static readonly ARROW_EDGE_MARGIN_PX = 14;
    private static instance: Tooltip | null = null; // portalweit gemeinsame Instanz

    private readonly tooltipElement: HTMLDivElement; // gemeinsamer Tooltip für alle Auslöser der Seite
    private readonly contentElement: HTMLDivElement; // Inhalt des aktiven Auslösers
    private activeTrigger: HTMLElement | null = null; // derzeit angezeigter Auslöser
    private suppressedTrigger: HTMLElement | null = null; // nach einem Klick erst beim erneuten Hover wieder öffnen
    private pendingTrigger: HTMLElement | null = null; // wartet auf die Einblendverzögerung
    private showTimeout: number | null = null; // geplanter Öffnungsvorgang
    private closeTimeout: number | null = null; // verzögertes Schließen beim Wechsel zum Tooltip

    /** Gibt die portalweit gemeinsame Tooltip-Instanz zurück */
    public static getInstance(): Tooltip {
        return this.instance ?? (this.instance = new Tooltip());
    }

    /** Kodiert ein HTML-Template für die Verwendung innerhalb von `data-tooltip` */
    public static html(strings: TemplateStringsArray, ...values: readonly unknown[]): string {
        return this.escapeHtmlAttribute(this.content(strings, ...values));
    }

    /** Kodiert bekannten statischen HTML-Inhalt für die Verwendung innerhalb von `data-tooltip` */
    public static htmlFromTrustedContent(html: string): string {
        return this.escapeHtmlAttribute(html.trim());
    }

    /** Erzeugt sicheren HTML-Inhalt für die programmatische Zuweisung an `dataset.tooltip` */
    public static content(strings: TemplateStringsArray, ...values: readonly unknown[]): string {
        let html = strings[0];

        values.forEach((value, index) => {
            html += this.escapeHtml(String(value ?? '')) + strings[index + 1];
        });

        return html.trim();
    }

    private static escapeHtml(value: string): string {
        return value
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    private static escapeHtmlAttribute(value: string): string {
        return this.escapeHtml(value)
            .replace(/\r?\n/g, '&#10;');
    }

    private constructor() {
        this.tooltipElement = document.createElement('div');
        this.tooltipElement.id = 'tooltip-generic';
        this.tooltipElement.className = 'tooltip-generic';
        this.tooltipElement.hidden = true;
        this.tooltipElement.setAttribute('role', 'tooltip');

        this.contentElement = document.createElement('div');
        this.contentElement.className = 'tooltip-generic-content';
        this.tooltipElement.append(this.contentElement);
        document.body.append(this.tooltipElement);

        this.initEvents();
    }

    /** Verknüpft Maus- und Stift-Hover sowie die Fensterposition mit dem Tooltip */
    private initEvents(): void {
        const showFromEvent = (event: PointerEvent): void => {
            if (event.pointerType === 'touch')
                return;

            const trigger = this.getTrigger(event.target);
            if (trigger === null || trigger === this.suppressedTrigger)
                return;

            this.cancelClose();
            if (trigger === this.activeTrigger && this.tooltipElement.hidden === false)
                return;

            if (this.activeTrigger !== null)
                this.close();
            this.scheduleShow(trigger, event.clientX, event.clientY);
        };

        document.addEventListener('pointerover', showFromEvent);
        document.addEventListener('pointerout', event => {
            if (event.pointerType === 'touch')
                return;

            const trigger = this.getTrigger(event.target);
            if (trigger === null)
                return;

            if (event.relatedTarget instanceof Node && trigger.contains(event.relatedTarget))
                return;

            if (trigger === this.pendingTrigger)
                this.cancelShow();
            if (trigger === this.suppressedTrigger)
                this.suppressedTrigger = null;
            if (trigger === this.activeTrigger)
                this.scheduleClose();
        });

        document.addEventListener('click', event => {
            const trigger = this.getTrigger(event.target);
            if (trigger !== null) {
                this.suppressedTrigger = trigger;
                this.cancelShow();
                this.close();
                return;
            }

            if (this.tooltipElement.contains(event.target as Node) === false)
                this.close();
        });

        this.tooltipElement.addEventListener('pointerover', () => this.cancelClose());
        this.tooltipElement.addEventListener('pointerout', () => this.scheduleClose());
        window.addEventListener('resize', () => this.position());
        window.addEventListener('scroll', () => this.position(), true);
    }

    /** Ermittelt den nächstgelegenen Tooltip-Auslöser außerhalb des Tooltips */
    private getTrigger(target: EventTarget | null): HTMLElement | null {
        if (!(target instanceof Element))
            return null;

        const trigger = target.closest<HTMLElement>('[data-tooltip]');
        if (trigger === null || this.tooltipElement.contains(trigger))
            return null;

        return trigger;
    }

    /** Zeigt den Tooltip erst nach anhaltendem Hover über demselben Auslöser */
    private scheduleShow(trigger: HTMLElement, pointerX: number, pointerY: number): void {
        this.cancelShow();
        this.pendingTrigger = trigger;
        this.showTimeout = window.setTimeout(() => {
            this.showTimeout = null;
            this.pendingTrigger = null;

            // Verhindert verspätete Tooltips hinter nachträglich geöffneten Dialogen
            const elementAtPointer = document.elementFromPoint(pointerX, pointerY);
            const isTriggerAtPointer = elementAtPointer !== null && (elementAtPointer === trigger || trigger.contains(elementAtPointer));
            if (trigger.isConnected && isTriggerAtPointer && trigger.matches(':hover') && trigger !== this.suppressedTrigger)
                this.show(trigger);
        }, Tooltip.SHOW_DELAY_MS);
    }

    private cancelShow(): void {
        if (this.showTimeout !== null)
            window.clearTimeout(this.showTimeout);

        this.showTimeout = null;
        this.pendingTrigger = null;
    }

    /** Setzt den HTML-Inhalt ein und zeigt den Tooltip am Auslöser an */
    private show(trigger: HTMLElement): void {
        if (trigger === this.activeTrigger && this.tooltipElement.hidden === false)
            return;

        const html = trigger.dataset.tooltip ?? '';
        if (html.trim() === '')
            return;

        this.close();
        this.activeTrigger = trigger;
        this.contentElement.innerHTML = html;
        this.tooltipElement.hidden = false;
        this.position();
    }

    /** Positioniert den Tooltip am Auslöser und innerhalb des sichtbaren Browserbereichs */
    private position(): void {
        if (this.activeTrigger === null || this.tooltipElement.hidden)
            return;

        const margin = Tooltip.VIEWPORT_MARGIN_PX;
        const gap = Tooltip.TRIGGER_GAP_PX;
        const trigger = this.activeTrigger;
        const triggerRect = trigger.getBoundingClientRect();
        const isTriggerVisible = trigger.isConnected && triggerRect.bottom > 0 && triggerRect.top < window.innerHeight && triggerRect.right > 0 && triggerRect.left < window.innerWidth;

        if (isTriggerVisible === false) {
            this.close();
            return;
        }

        const tooltipRect = this.tooltipElement.getBoundingClientRect();
        const availableAbove = triggerRect.top - margin;
        const availableBelow = window.innerHeight - triggerRect.bottom - margin;
        const showAbove = availableAbove >= tooltipRect.height || availableAbove > availableBelow;
        const preferredTop = showAbove ? triggerRect.top - tooltipRect.height - gap : triggerRect.bottom + gap;
        const top = Math.max(margin, Math.min(preferredTop, window.innerHeight - tooltipRect.height - margin));
        const preferredLeft = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
        const left = Math.max(margin, Math.min(preferredLeft, window.innerWidth - tooltipRect.width - margin));
        const arrowLeft = Math.max(Tooltip.ARROW_EDGE_MARGIN_PX, Math.min(triggerRect.left + triggerRect.width / 2 - left, tooltipRect.width - Tooltip.ARROW_EDGE_MARGIN_PX));

        this.tooltipElement.dataset.position = showAbove ? 'top' : 'bottom';
        this.tooltipElement.style.setProperty('--tooltip-generic-arrow-left', `${arrowLeft}px`);
        this.tooltipElement.style.top = `${top}px`;
        this.tooltipElement.style.left = `${left}px`;
    }

    /** Schließt erst nach einem möglichen Wechsel vom Auslöser in den Tooltip */
    private scheduleClose(): void {
        this.cancelClose();
        this.closeTimeout = window.setTimeout(() => {
            const isHovered = this.activeTrigger?.matches(':hover') === true || this.tooltipElement.matches(':hover');
            if (isHovered === false)
                this.close();
        }, Tooltip.CLOSE_DELAY_MS);
    }

    private cancelClose(): void {
        if (this.closeTimeout === null)
            return;

        window.clearTimeout(this.closeTimeout);
        this.closeTimeout = null;
    }

    private close(): void {
        this.cancelShow();
        this.cancelClose();
        this.tooltipElement.hidden = true;
        this.activeTrigger = null;
    }
}

/** Einheitliche Tooltip-Inhalte für wiederkehrende Schülermerkmale */
export const TOOLTIP_INHALTE = {
    sonderpaedagogischerFoerderbedarf: Tooltip.html`
        <p><strong>Sonderpädagogischer Förderbedarf</strong></p>
        <p>Schülerinnen und Schüler mit diesem Merkmal gehen nicht in aggregierte Rückmeldungen ein.</p>
    `,
    unzureichendeSprachbeherrschung: Tooltip.html`
        <p><strong>Unzureichende Sprachbeherrschung</strong></p>
        <p>Schülerinnen und Schüler mit diesem Merkmal gehen nicht in aggregierte Rückmeldungen ein.</p>
    `,
    sonderpaedagogischerUnterstuetzungsbedarf: Tooltip.html`
        <p><strong>Sonderpädagogischer Unterstützungsbedarf</strong></p>
    `,
    teilleistungsstoerungMathematik: Tooltip.html`
        <p><strong>Teilleistungsstörung Mathematik</strong></p>
    `,
    teilleistungsstoerungDeutsch: Tooltip.html`
        <p><strong>Teilleistungsstörung Deutsch</strong></p>
    `,
    deutschNichtDominanteSprache: Tooltip.html`
        <p><strong>Deutsch als nicht dominante Sprache</strong></p>
    `,
    klassenwiederholung: Tooltip.html`
        <p><strong>Klassenwiederholung</strong></p>
    `
} as const;
